import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Thumbnail from 'common/components/thumbnail';
import FilesPreview from 'common/components/thumbnail/FilesPreview';
import { USER_CONSTANTS } from 'constants/users';
import { ReactComponent as UploadAttachmentIcon } from 'images/icons/akar-icons_plus.svg';
import { useTicketAttachments } from 'remote-state/attachments';
import { useUserInfo } from 'remote-state/userServiceHooks';
import { baseURL } from 'services/axiosBaseInstance';
import { selectActiveUploads, clearFailedUploads } from 'store/attachmentsSlice';

import { QUERIES_KEYS } from 'constant';
import { getDate } from 'common/utils/dateTimeUtils';
import { AddAttachmentButton } from './addAttachmentButton';
import { StyledFileInputLabel } from './addAttachmentButton/style';
import { useUploadAttachments } from './hooks/useUploadAttachments';
import { ProgressBar } from './progressBar';
import { StyledContainer } from './style';
import { AttachmentPath } from '../../../../services/attachments';

const Attachments = ({ disabled, showTicketAsTab }) => {
  const activeUploads = useSelector(selectActiveUploads);
  const [selectedFileIndex, setSelectedImageIndex] = useState(0);
  const [displayPreview, setDisplayPreview] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => () => dispatch(clearFailedUploads()), [dispatch]);

  const {
    srAttachments: { data: attachments = [] },
    updateSRAttachmentsAfterDelete,
  } = useTicketAttachments();
  const { handleActiveUploads, handleUploadNewAttachment } = useUploadAttachments({ showTicketAsTab });

  const userInfo = useUserInfo(QUERIES_KEYS.CURRENT_USER_FROM_CORE);
  const { valueCaption: timezone } = userInfo?.data?.info?.find((atr) => atr.key === USER_CONSTANTS.TIMEZONE) || {};
  const { valueCaption: dateFormat } =
    userInfo?.data?.info?.find((atr) => atr.key === USER_CONSTANTS.DATE_FORMAT) || {};

  const handleClose = (id) => {
    handleActiveUploads({ id, type: 'removeUpload' });
  };

  const handleMouseUp = () => {
    // SR: Waiting to be used while developing a markup thumbnail with the mouse
    // console.log(`Selected text: ${window.getSelection()}`);
  };

  const handleDeleteAttachment = (attachmentId) => {
    updateSRAttachmentsAfterDelete(attachmentId);
  };
  const handleDisplayPreview = (index) => {
    setSelectedImageIndex(index);
    setDisplayPreview(true);
  };

  const filesData = [];
  for (const attachment of attachments) {
    const {
      attachment: {
        metadata: { id: attachmentId, originalName, extension, userName: username, createdDate, fileId },
      },
      count,
    } = attachment;
    const fileUrl = `${baseURL}${AttachmentPath.V3}${attachmentId}/payload`;
    filesData.push({
      key: attachmentId,
      attachmentId,
      fileId,
      src: fileUrl,
      fileName: originalName,
      extension,
      userName: username,
      createdDate,
      removeParams: { fileUrl },
      attachmentUsageCount: count,
    });
  }

  const thumbnails = filesData.map((file, index) => {
    const { key, attachmentId, fileName, extension, src, userName, createdDate, attachmentUsageCount, fileId } = file;
    return (
      <Thumbnail
        key={key}
        attachmentId={attachmentId}
        extendedFileName={fileName}
        extension={extension}
        username={userName}
        fileId={fileId}
        timestamp={getDate(timezone, dateFormat, createdDate)}
        fileUrl={src}
        onDisplayPreview={handleDisplayPreview}
        onAttachmentDeleted={handleDeleteAttachment}
        index={index}
        attachmentUsageCount={attachmentUsageCount}
        disabled={disabled}
      />
    );
  });

  const handleImageRemove = () => {
    setDisplayPreview(false);
  };

  return (
    <StyledContainer onMouseUp={handleMouseUp}>
      {!disabled && (
        <StyledFileInputLabel>
          <AddAttachmentButton
            btnComponent={UploadAttachmentIcon}
            handleUploadNewAttachment={handleUploadNewAttachment}
          />
        </StyledFileInputLabel>
      )}
      {thumbnails}
      {activeUploads.map((upload) => (
        <ProgressBar
          key={upload.id}
          id={upload.id}
          fileName={upload.fileName}
          uploadProgress={upload.progress}
          error={upload.error}
          onClose={handleClose}
        />
      ))}
      {displayPreview && (
        <FilesPreview
          filesData={filesData}
          selectedFileIndex={selectedFileIndex}
          onClose={() => setDisplayPreview(false)}
          onFileRemove={handleImageRemove}
        />
      )}
    </StyledContainer>
  );
};

export default Attachments;
